import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * hallmark critique · DocsPanel
 * tokens locked from docs/design.md §1 (colors) + §5 (pill nav)
 * states covered: idle · fetching · ok · error
 * mobile verified: 320 / 375 / 414 / 768
 * no fake chrome · no italic headers · single clay accent per viewport
 *
 * Phase 14e — right-rail content for the "Docs" nav tab.
 *
 * Purpose: give the on-stage operator and the app dev a hands-on view of
 * the API surface without leaving the dashboard. Three sections:
 *
 * 1. Companion API playground — form + live response for a rider
 * 2. Route status probe — pick a route short_name, get live counts
 * 3. Endpoint index + external links (Swagger /docs, MCP /mcp/info)
 */
import { useCallback, useState } from "react";
import { getBaseUrl } from "../../lib/api";
import { colors } from "../../lib/tokens";
import { useMumbaiRoutes } from "../../hooks/useMumbaiRoutes";
// ---------------------------------------------------------------------
// Small primitives
// ---------------------------------------------------------------------
function SectionTitle({ children }) {
    return (_jsx("div", { style: {
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: colors.coolAsh,
            marginBottom: 6,
        }, children: children }));
}
function Field({ label, children, }) {
    return (_jsxs("label", { style: { display: "block", marginBottom: 8 }, children: [_jsx("div", { style: {
                    fontSize: 10,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: colors.coolAsh,
                    marginBottom: 3,
                }, children: label }), children] }));
}
const inputStyle = {
    width: "100%",
    padding: "6px 10px",
    border: `1px solid ${colors.pebble}`,
    borderRadius: 4,
    fontFamily: "var(--font-mono, ui-monospace, monospace)",
    fontSize: 12,
    background: colors.pureWhite,
    color: colors.deepInk,
    boxSizing: "border-box",
};
const buttonStyle = {
    padding: "6px 14px",
    border: 0,
    borderRadius: 1000,
    background: colors.deepInk,
    color: colors.pureWhite,
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    cursor: "pointer",
};
// ---------------------------------------------------------------------
// Section 1 · Companion API playground
// ---------------------------------------------------------------------
function CompanionPlayground() {
    const [phone, setPhone] = useState("+919812345678");
    const [lat, setLat] = useState(19.0453);
    const [lng, setLng] = useState(73.0685);
    const [destination, setDestination] = useState("MMRDA BKC");
    const [preferAc, setPreferAc] = useState(true);
    const [state, setState] = useState("idle");
    const [response, setResponse] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const call = useCallback(async () => {
        setState("loading");
        setErrorMsg(null);
        try {
            const res = await fetch(`${getBaseUrl()}/api/companion/recommendation`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    phone,
                    location: { lat, lng },
                    destination_stop: destination || undefined,
                    prefer_ac: preferAc,
                    near_radius_m: 6000,
                }),
            });
            if (!res.ok)
                throw new Error(`HTTP ${res.status}`);
            setResponse(await res.json());
            setState("ok");
        }
        catch (e) {
            setErrorMsg(String(e));
            setState("error");
        }
    }, [phone, lat, lng, destination, preferAc]);
    const preset = (label, values) => (_jsx("button", { type: "button", onClick: values, style: {
            padding: "3px 8px",
            border: `1px solid ${colors.pebble}`,
            borderRadius: 1000,
            background: colors.pureWhite,
            color: colors.deepInk,
            fontSize: 10,
            cursor: "pointer",
        }, children: label }));
    return (_jsxs("div", { children: [_jsx(SectionTitle, { children: "Companion API \u00B7 POST /api/companion/recommendation" }), _jsx("div", { style: { fontSize: 11, color: colors.coolAsh, marginBottom: 10 }, children: "Try the personalised recommendation. Different phones at the same location get different rankings (deterministic per phone hash)." }), _jsxs("div", { style: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }, children: [preset("Kharghar office", () => {
                        setPhone("+919812345678");
                        setLat(19.0453);
                        setLng(73.0685);
                        setDestination("MMRDA BKC");
                        setPreferAc(true);
                    }), preset("Panvel → Alibag", () => {
                        setPhone("+917000000000");
                        setLat(18.9906);
                        setLng(73.1157);
                        setDestination("Alibag ST Stand");
                        setPreferAc(false);
                    }), preset("Karjat", () => {
                        setPhone("+918888888888");
                        setLat(18.9107);
                        setLng(73.3287);
                        setDestination("");
                        setPreferAc(false);
                    })] }), _jsx(Field, { label: "phone", children: _jsx("input", { type: "text", value: phone, onChange: (e) => setPhone(e.target.value), style: inputStyle }) }), _jsxs("div", { style: { display: "flex", gap: 8 }, children: [_jsx("div", { style: { flex: 1 }, children: _jsx(Field, { label: "lat", children: _jsx("input", { type: "number", step: "0.0001", value: lat, onChange: (e) => setLat(parseFloat(e.target.value)), style: inputStyle }) }) }), _jsx("div", { style: { flex: 1 }, children: _jsx(Field, { label: "lng", children: _jsx("input", { type: "number", step: "0.0001", value: lng, onChange: (e) => setLng(parseFloat(e.target.value)), style: inputStyle }) }) })] }), _jsx(Field, { label: "destination_stop (optional)", children: _jsx("input", { type: "text", value: destination, onChange: (e) => setDestination(e.target.value), style: inputStyle }) }), _jsxs("label", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: 12, color: colors.deepInk }, children: [_jsx("input", { type: "checkbox", checked: preferAc, onChange: (e) => setPreferAc(e.target.checked) }), "prefer AC"] }), _jsx("button", { type: "button", style: buttonStyle, onClick: call, disabled: state === "loading", children: state === "loading" ? "…" : "Recommend" }), state === "error" && (_jsx("div", { style: {
                    marginTop: 10,
                    padding: 8,
                    background: colors.clayEmber,
                    color: colors.pureWhite,
                    fontSize: 11,
                    borderRadius: 4,
                }, children: errorMsg })), state === "ok" && response !== null && (_jsx(ResponsePreview, { data: response, maxLines: 22 }))] }));
}
// ---------------------------------------------------------------------
// Section 2 · Route status probe
// ---------------------------------------------------------------------
function RouteStatusProbe() {
    const routes = useMumbaiRoutes();
    const [selected, setSelected] = useState("");
    const [detail, setDetail] = useState(null);
    const [state, setState] = useState("idle");
    const call = useCallback(async () => {
        if (!selected)
            return;
        setState("loading");
        try {
            const res = await fetch(`${getBaseUrl()}/api/mumbai/routes/${selected}`);
            if (!res.ok)
                throw new Error(`HTTP ${res.status}`);
            setDetail(await res.json());
            setState("ok");
        }
        catch {
            setState("error");
        }
    }, [selected]);
    const uniqueByShort = Object.values(routes.byId).reduce((acc, r) => {
        if (!acc[r.route_id])
            acc[r.route_id] = r;
        return acc;
    }, {});
    return (_jsxs("div", { style: { marginTop: 20 }, children: [_jsxs(SectionTitle, { children: ["Routes \u00B7 GET /api/mumbai/routes/", "{", "route_id", "}"] }), _jsxs("div", { style: { fontSize: 11, color: colors.coolAsh, marginBottom: 10 }, children: [routes.count, " curated routes. Pick one to fetch its full stop sequence + operator + distance/duration/headway."] }), _jsx(Field, { label: "route_id", children: _jsxs("select", { value: selected, onChange: (e) => setSelected(e.target.value), style: inputStyle, children: [_jsx("option", { value: "", children: "\u2014 select \u2014" }), Object.values(uniqueByShort)
                            .sort((a, b) => a.operator.localeCompare(b.operator))
                            .map((r) => (_jsxs("option", { value: r.route_id, children: [r.operator, " \u00B7 ", r.short_name, " \u00B7 d", r.direction_id, " \u00B7 ", r.route_id] }, r.route_id)))] }) }), _jsx("button", { type: "button", style: buttonStyle, onClick: call, disabled: state === "loading" || !selected, children: "Fetch" }), state === "ok" && detail !== null && (_jsx(ResponsePreview, { data: detail, maxLines: 16 }))] }));
}
// ---------------------------------------------------------------------
// Section 3 · Endpoint reference + external links
// ---------------------------------------------------------------------
function EndpointList() {
    const base = getBaseUrl();
    const rows = [
        { method: "GET", path: "/api/vehicles/live?at=<epoch>", note: "envelope at any timestamp" },
        { method: "GET", path: "/api/vehicles/{vehicle_id}", note: "single bus + route + hold state" },
        { method: "GET", path: "/api/vehicles/{vehicle_id}/timeline?from=&to=&step=", note: "pre-computed positions" },
        { method: "GET", path: "/api/mumbai/routes", note: "all 20 curated routes" },
        { method: "GET", path: "/api/mumbai/routes/{route_id}", note: "single route + stops" },
        { method: "POST", path: "/api/companion/recommendation", note: "hyper-personalised bus for a phone" },
        { method: "POST", path: "/api/hold", note: "issue a hold (audit-logged, freezes bus)" },
        { method: "GET", path: "/api/hold?limit=20", note: "recent holds" },
        { method: "GET/POST", path: "/api/sim/playback[/play|pause|step|reset]", note: "video-player scrubber" },
        { method: "GET", path: "/api/metrics/headway_cv", note: "bunching CV vs UCLA baseline" },
        { method: "GET", path: "/api/cctv/signals", note: "YOLO occupancy + waiting counts" },
        { method: "POST", path: "/api/phantom/request", note: "spawn phantom bus (DARP)" },
        { method: "WS", path: "/ws/vehicles", note: "3s push driven by sim clock" },
        { method: "GET", path: "/mcp/info", note: "MCP server + 5-tool metadata" },
        { method: "GET", path: "/mcp/sse", note: "MCP over SSE" },
    ];
    return (_jsxs("div", { style: { marginTop: 20 }, children: [_jsx(SectionTitle, { children: "Endpoints" }), _jsxs("div", { style: {
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    marginBottom: 10,
                }, children: [_jsx("a", { href: `${base}/docs`, target: "_blank", rel: "noreferrer", style: {
                            padding: "4px 10px",
                            borderRadius: 1000,
                            background: colors.clayEmber,
                            color: colors.pureWhite,
                            fontSize: 11,
                            fontWeight: 700,
                            textDecoration: "none",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }, children: "Open Swagger" }), _jsx("a", { href: `${base}/mcp/info`, target: "_blank", rel: "noreferrer", style: {
                            padding: "4px 10px",
                            borderRadius: 1000,
                            background: colors.deepInk,
                            color: colors.pureWhite,
                            fontSize: 11,
                            fontWeight: 700,
                            textDecoration: "none",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }, children: "MCP Info" }), _jsx("a", { href: `${base}/openapi.json`, target: "_blank", rel: "noreferrer", style: {
                            padding: "4px 10px",
                            borderRadius: 1000,
                            border: `1px solid ${colors.pebble}`,
                            background: colors.pureWhite,
                            color: colors.deepInk,
                            fontSize: 11,
                            textDecoration: "none",
                        }, children: "openapi.json" })] }), _jsx("div", { style: { fontFamily: "var(--font-mono, ui-monospace, monospace)", fontSize: 11 }, children: rows.map((r, i) => (_jsxs("div", { style: {
                        display: "flex",
                        gap: 8,
                        padding: "4px 0",
                        borderBottom: `1px dashed ${colors.pebble}`,
                    }, children: [_jsx("span", { style: {
                                minWidth: 48,
                                color: r.method.startsWith("G")
                                    ? "#5FA05A"
                                    : r.method === "WS"
                                        ? "#6B7280"
                                        : colors.clayEmber,
                                fontWeight: 700,
                            }, children: r.method }), _jsx("span", { style: { flex: 1, color: colors.deepInk, wordBreak: "break-word" }, children: r.path }), _jsx("span", { style: { color: colors.coolAsh, whiteSpace: "nowrap" }, children: r.note })] }, i))) })] }));
}
function ResponsePreview({ data, maxLines }) {
    const text = JSON.stringify(data, null, 2);
    const lines = text.split("\n");
    const truncated = lines.length > maxLines;
    const shown = truncated ? [...lines.slice(0, maxLines), `… (${lines.length} lines total)`] : lines;
    return (_jsx("pre", { style: {
            marginTop: 10,
            padding: 10,
            background: "#0f1418",
            color: "#c9e1e6",
            fontSize: 11,
            lineHeight: 1.4,
            borderRadius: 4,
            overflowX: "auto",
            maxHeight: 320,
            overflowY: "auto",
        }, children: shown.join("\n") }));
}
// ---------------------------------------------------------------------
// Composed panel
// ---------------------------------------------------------------------
export function DocsPanel() {
    return (_jsxs("div", { "data-testid": "docs-panel", style: {
            padding: 16,
            height: "100%",
            overflowY: "auto",
            backgroundColor: colors.pureWhite,
            fontFamily: "var(--font-display)",
        }, children: [_jsxs("div", { style: { marginBottom: 18 }, children: [_jsx("div", { style: {
                            fontSize: 10,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: colors.coolAsh,
                        }, children: "API Playground" }), _jsx("div", { style: {
                            fontSize: 20,
                            fontWeight: 700,
                            color: colors.deepInk,
                            lineHeight: 1.15,
                            marginTop: 2,
                        }, children: "Try every endpoint we ship." }), _jsx("div", { style: {
                            fontSize: 12,
                            color: colors.coolAsh,
                            marginTop: 4,
                            lineHeight: 1.4,
                        }, children: "Interactive playground for the two most-called endpoints, plus direct links to Swagger and the MCP info page. Every response here is real \u2014 no mocks, no cached data." })] }), _jsx(CompanionPlayground, {}), _jsx(RouteStatusProbe, {}), _jsx(EndpointList, {})] }));
}
